import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { commonQueryKeys } from "@/common/data/commonQueryKeys";
import api from "@/common/utils/api";
import { ErrorResponse } from "@reflag/shared/api";
import {
  EnvironmentDTO,
  EnvironmentPatchArgs,
} from "@reflag/shared/environmentAPI";

import { environmentQueryKeys } from "./environmentQueryKeys";

type EnvironmentPatchArgsWithId = EnvironmentPatchArgs & {
  id: string;
};

export function useEnvironmentUpdateMutation(
  options: Omit<
    UseMutationOptions<
      EnvironmentDTO,
      AxiosError<ErrorResponse>,
      EnvironmentPatchArgsWithId
    >,
    "mutationKey" | "mutationFn"
  > = {},
) {
  const { appId, orgId } = useEnvironment();
  const queryClient = useQueryClient();
  const { onSuccess, ...otherOptions } = options;

  return useMutation({
    mutationKey: ["environment"],

    mutationFn: async (data) => {
      const { id, ...updatedData } = data;
      return api
        .patch<"/apps/:appId/environments/:envId">(
          `/apps/${appId}/environments/${id}`,
          updatedData,
        )
        .then((res) => res.data);
    },

    onSuccess: (updatedEnvironment, data, context) => {
      queryClient.invalidateQueries({
        queryKey: commonQueryKeys.bootstrap(orgId),
      });
      queryClient.invalidateQueries({
        queryKey: environmentQueryKeys.list(appId),
      });

      onSuccess?.(updatedEnvironment, data, context);
    },

    ...otherOptions,
  });
}
